/*!
 * Copyright © 2026. Verizon Connect Ireland Limited. All rights reserved.
 */

import { EOverrideStatus } from './override-status.enum';
import { EOverrideVisualStatus } from './override-visual-status.enum';
import { OVERRIDE_VISUAL_CONFIG } from './override-visual.constants';
import { IOverrideVisualization } from './override-visualization.interface';
import { IPostedSpeedUpdate } from './posted-speed-update.interface';

/** Maps the store status to the visual status enum. */
export const STATUS_MAP: Record<string, EOverrideVisualStatus> = {
  [EOverrideStatus.ACTIVE]: EOverrideVisualStatus.ACTIVE,
  [EOverrideStatus.PLANNED]: EOverrideVisualStatus.PLANNED,
  [EOverrideStatus.EXPIRED]: EOverrideVisualStatus.EXPIRED
};

/**
 * Combines override data with the selected segment reference to produce
 * visualization instructions for the map renderer.
 *
 * Filters out overrides without valid geometry (null, undefined, or fewer
 * than 2 points) and excludes the override matching the primary selection.
 * Also excludes the override currently being edited (rendered separately
 * with the solid segment-highlight style).
 * Maps the API-provided status to the visual color. Stamps `isHovered` and
 * `isSelected` flags based on the provided hovered and selected update IDs.
 *
 * @param overrides - The array of posted speed updates from the store.
 * @param selectedSegmentRef - The currently selected segment reference, or null.
 * @param hoveredUpdateId - The ID of the currently hovered override, or null.
 * @param selectedUpdateId - The ID of the currently selected override, or null.
 * @param editingOverrideId - The ID of the override being edited, or null.
 * @returns An array of {@link IOverrideVisualization} objects ready for rendering.
 */
export function buildOverrideVisualizations(
  overrides: IPostedSpeedUpdate[],
  selectedSegmentRef: string | null,
  hoveredUpdateId: string | null,
  selectedUpdateId: string | null,
  editingOverrideId: string | null = null
): IOverrideVisualization[] {
  const result: IOverrideVisualization[] = [];

  for (const override of overrides) {
    if (!override.geometry || override.geometry.length < 2) {
      continue;
    }

    if (selectedSegmentRef != null && override.segmentRef === selectedSegmentRef) {
      continue;
    }

    if (editingOverrideId != null && override.id === editingOverrideId) {
      continue;
    }

    const status = STATUS_MAP[override.status];
    if (!status) {
      continue;
    }

    result.push({
      id: override.id,
      status,
      color: OVERRIDE_VISUAL_CONFIG[status].color,
      geometry: override.geometry,
      segmentGeometries: override.segmentGeometries,
      isHovered: override.id === hoveredUpdateId,
      isSelected: override.id === selectedUpdateId
    });
  }

  return result;
}
